/**
 * Highest `resultsLimit` Geotab accepts per entity. A request above the cap is rejected or silently
 * clamped (TachographDataFile returns at most 5000 whatever limit is asked), and a
 * request at the cap can come back as a full page with more data behind it: callers detect a full
 * page (paginate or show a truncation warning).
 *
 * A PR whose entity is used only by its own page appends its constant here; no new local
 * literals.
 */

/** Documented caps (Geotab API reference / GetFeed "Maximum results"). */
export const RESULTS_LIMIT_TRIP = 25_000;
export const RESULTS_LIMIT_DEVICE = 5_000;
export const RESULTS_LIMIT_USER = 5_000;
/** Device pickers ask for fewer than the cap on purpose: a search narrows faster than a long list scrolls. */
export const RESULTS_LIMIT_DEVICE_PICKER = 1_000;
/**
 * `User` is read in pages sorted by id (`fetchAllSortedById`), at most this many. 20 pages = 100000
 * users, about 2.5x the largest database today (about 40000 driver users incl. archived, 8 pages);
 * a read that reaches {@link MAX_USERS_LOADED} may be truncated.
 */
export const MAX_USER_PAGES = 20;
export const MAX_USERS_LOADED = RESULTS_LIMIT_USER * MAX_USER_PAGES;
/**
 * `Device` read the same way (logbook-audit vehicle list). 4 pages = 20000 vehicles, the largest
 * database today has 7339 active (deutschebahn, 17.10.2026); reaching {@link MAX_DEVICES_LOADED}
 * may be truncated.
 */
export const MAX_DEVICE_PAGES = 4;
export const MAX_DEVICES_LOADED = RESULTS_LIMIT_DEVICE * MAX_DEVICE_PAGES;
export const RESULTS_LIMIT_FILL_UP = 10_000;
export const RESULTS_LIMIT_RULE = 10_000;
export const RESULTS_LIMIT_MEDIA_FILE = 10_000;
export const RESULTS_LIMIT_STATUS_DATA = 50_000;
export const RESULTS_LIMIT_LOG_RECORD = 50_000;

/** Cap not documented or verified; 50000 has been accepted so far. */
export const RESULTS_LIMIT_FUEL_USED = 50_000;
export const RESULTS_LIMIT_FUEL_TRANSACTION = 50_000;
export const RESULTS_LIMIT_TRIP_ANNOTATION = 50_000;
export const RESULTS_LIMIT_ADD_IN_DATA = 50_000;
/** Cap not documented or verified. */
export const RESULTS_LIMIT_PROPERTY = 10_000;

/**
 * ChargeEvent and FuelUpEvent: read per vehicle by the energy report and the audit.
 * ChargeEvent: live-verified silent clamp at 50000 (API Runner 2026-10-14, limit 50001 returned
 * 50000 rows). Not documented. Raised from 10000 so per-device reads match the real cap.
 */
export const RESULTS_LIMIT_CHARGE_EVENT = 50_000;
export const RESULTS_LIMIT_FUEL_UP_EVENT = 10_000;

/**
 * Group has no documented cap. 10000 has been accepted in production; not yet verified in the
 * API Runner.
 */
export const RESULTS_LIMIT_GROUP = 10_000;

/**
 * Tachograph page. Checked in the API Runner (2026-10): TachographDataFile is silently clamped to
 * 5000 rows per request (larger limits are accepted but return at most 5000), so a full page of
 * 5000 means truncated. EntitySetting needs a search and has no verified cap.
 */
export const RESULTS_LIMIT_TACHOGRAPH_DATA_FILE = 5_000;
/**
 * Conservative on purpose: TachographCompanyCard accepted 50001 without error, but a page clamped
 * below the requested limit hides the truncation (as TachographDataFile does at 5000). At 5000 a
 * full page is flagged as long as the real cap is not lower. Not live-verified whether it clamps.
 */
export const RESULTS_LIMIT_TACHOGRAPH_COMPANY_CARD = 5_000;
export const RESULTS_LIMIT_ENTITY_SETTING = 10_000;

/** https://developers.geotab.com/myGeotab/apiReference/objects/FaultData */
export const RESULTS_LIMIT_FAULT_DATA = 50_000;
/**
 * Pages of `RESULTS_LIMIT_FAULT_DATA` the fleet dashboard reads (250000 faults) before it stops
 * and warns that the result may be incomplete.
 */
export const MAX_FAULT_DATA_PAGES = 5;
/** https://developers.geotab.com/myGeotab/apiReference/objects/ExceptionEvent */
export const RESULTS_LIMIT_EXCEPTION_EVENT = 50_000;
/** https://developers.geotab.com/myGeotab/apiReference/objects/Diagnostic */
export const RESULTS_LIMIT_DIAGNOSTIC = 1_300_000;
/**
 * The vehicle data check's read of the catalog by kind (`diagnosticTypes`: GoDiagnostic,
 * DataDiagnostic). A full page means more entries than were read, and the page warns.
 */
export const RESULTS_LIMIT_DIAGNOSTIC_TYPE_SEARCH = 50_000;

/**
 * Cap undocumented; limit 50001 accepted live 2026-10-14, true max unknown. Entities:
 * https://developers.geotab.com/myGeotab/apiReference/objects/<Entity>
 * If Geotab starts rejecting a lower cap, the Get fails, and with it every call batched into the
 * same `ExecuteMultiCall` by `@fairfleet/geotab`.
 */
export const RESULTS_LIMIT_SOURCE = 50_000;
export const RESULTS_LIMIT_DISPLAY_UNIT_OF_MEASURE = 50_000;
export const RESULTS_LIMIT_DISPLAY_MEASUREMENT_PROFILE_DETAIL = 50_000;
export const RESULTS_LIMIT_RANGE_ESTIMATE = 50_000;
/**
 * Feedback has no object page and is not listed on the GetFeed method page (checked 2026-10-14),
 * so the general "maximum value is 50,000 unless otherwise stated" applies.
 */
export const RESULTS_LIMIT_FEEDBACK = 50_000;
export const RESULTS_LIMIT_EXCEPTION_EVENT_COMMENT = 50_000;
export const RESULTS_LIMIT_MAINTENANCE_WORK_ORDER = 50_000;
export const RESULTS_LIMIT_MAINTENANCE_WORK_ORDER_JOB = 50_000;
export const RESULTS_LIMIT_MAINTENANCE_WORK_REQUEST = 50_000;
